import { useState } from "react";

import { useSubmitReview } from "@/api/review";
import type {
  DraftBatchItem,
  DraftBatchResponse,
  ReviewSubmitDecision,
  ReviewSubmitResponse,
} from "@/api/types";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { formatJobNumber } from "@/lib/format";

import { BulkActions } from "./BulkActions";
import { ReviewRow } from "./ReviewRow";

/**
 * One batch's worth of HITL review UI — the per-job panel rendered by
 * both the standalone `/jobs/:id/review` route and the cross-job
 * `/review` queue. Owns its own in-session decision state and submit
 * mutation so multiple panels on the queue page submit independently
 * (each batch is its own commit boundary at the orchestrator).
 *
 * Already-decided rows render as locked with the prior decision shown —
 * the audit_log invariant means re-decision is blocked server-side too.
 *
 * `onAfterSubmit` is invoked after a successful submit. The standalone
 * route uses it to navigate back to `/jobs/:id` when the rollup
 * transitions the job to a terminal state. The queue page leaves it
 * unset — the `["review-pending"]` invalidation refetches the queue and
 * the rolled-up batch simply drops out.
 */
export interface ReviewPanelProps {
  batch: DraftBatchResponse;
  onAfterSubmit?: (result: ReviewSubmitResponse) => void;
}

export function ReviewPanel({ batch, onAfterSubmit }: ReviewPanelProps) {
  const submitMutation = useSubmitReview(batch.jobId);

  // In-session decisions keyed by draftId. Cleared on successful submit
  // because the next render gets the locked overlay from the GET.
  const [localDecisions, setLocalDecisions] = useState<Map<string, "approved" | "rejected">>(
    () => new Map(),
  );

  // Pending-promotion warning carried from the most-recent submit
  // response. Non-zero means the orchestrator's eager
  // `appendCatalogueRows` threw and the rollup safety-net will retry
  // those rows at batch completion. The user is not blocked; the
  // warning is purely informational. Cleared on the next submit.
  const [pendingPromotionCount, setPendingPromotionCount] = useState(0);

  const items = batch.items;
  const total = items.length;
  const priorDecided = items.filter((i) => i.existingDecision !== null).length;
  const sessionDecided = localDecisions.size;
  const decided = priorDecided + sessionDecided;
  const remaining = total - decided;
  const isFinalised =
    batch.jobStatus !== "pending_review" && batch.jobStatus !== "validating";

  const handleDecide = (item: DraftBatchItem, decision: "approved" | "rejected") => {
    setLocalDecisions((prev) => {
      const next = new Map(prev);
      next.set(item.draftId, decision);
      return next;
    });
  };

  const handleApplyAll = (decision: "approved" | "rejected") => {
    setLocalDecisions((prev) => {
      const next = new Map(prev);
      for (const item of items) {
        if (item.existingDecision === null && !next.has(item.draftId)) {
          next.set(item.draftId, decision);
        }
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    const decisions: ReviewSubmitDecision[] = [];
    for (const [draftId, decision] of localDecisions) {
      decisions.push({ draftId, decision });
    }
    if (decisions.length === 0) return;
    // Clear the prior submit's warning so a 409/502 on this attempt
    // doesn't leave a stale "pending promotion" banner sitting above
    // the error. The badge state on already-decided rows is the
    // durable record of pending-promotion across submits — the banner
    // is purely a per-submit acknowledgement.
    setPendingPromotionCount(0);
    const result = await submitMutation.mutateAsync({ decisions });
    setLocalDecisions(new Map());
    setPendingPromotionCount(result.promotionPending);
    onAfterSubmit?.(result);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <p className="label-mono">Review batch</p>
          <CardTitle
            className="font-mono text-lg break-all"
            title={batch.jobId}
          >
            #{formatJobNumber(batch.jobNumber)}
          </CardTitle>
          <CardDescription>
            {total} total ·{" "}
            <span className="text-text">{decided} decided</span> ·{" "}
            <span className={remaining === 0 ? "text-emerald-300" : "text-accent"}>
              {remaining} remaining
            </span>
            {batch.reviewDeadline ? (
              <>
                {" · "}
                <DeadlineCountdown deadline={batch.reviewDeadline} />
              </>
            ) : null}
          </CardDescription>
        </CardHeader>
        {!isFinalised ? (
          <CardContent>
            <BulkActions
              items={items}
              localDecisions={localDecisions}
              onApplyAll={handleApplyAll}
            />
          </CardContent>
        ) : null}
      </Card>

      {isFinalised ? (
        <Alert>
          This batch is finalised (job status: {batch.jobStatus}). Decisions
          below are read-only.
        </Alert>
      ) : null}

      <ol className="space-y-3">
        {items.map((item) => (
          <li key={item.draftId}>
            <ReviewRow
              item={item}
              decision={localDecisions.get(item.draftId) ?? null}
              onDecide={(d) => handleDecide(item, d)}
            />
          </li>
        ))}
      </ol>

      {pendingPromotionCount > 0 ? (
        <Alert>
          <span className="text-amber-300">
            {pendingPromotionCount} row{pendingPromotionCount === 1 ? "" : "s"}{" "}
            approved — promotion to the catalogue is pending. The rollup
            safety-net will retry automatically when the batch finishes.
          </span>
        </Alert>
      ) : null}

      {!isFinalised ? (
        <SubmitBar
          decisionCount={localDecisions.size}
          approvedCount={countByDecision(localDecisions, "approved")}
          rejectedCount={countByDecision(localDecisions, "rejected")}
          isSubmitting={submitMutation.isPending}
          submitError={submitMutation.error}
          onSubmit={() => void handleSubmit()}
        />
      ) : null}
    </div>
  );
}

function countByDecision(
  decisions: ReadonlyMap<string, "approved" | "rejected">,
  target: "approved" | "rejected",
): number {
  let count = 0;
  for (const value of decisions.values()) {
    if (value === target) count += 1;
  }
  return count;
}

function SubmitBar(props: {
  decisionCount: number;
  approvedCount: number;
  rejectedCount: number;
  isSubmitting: boolean;
  submitError: unknown;
  onSubmit: () => void;
}) {
  const { decisionCount, approvedCount, rejectedCount, isSubmitting, submitError, onSubmit } =
    props;
  const errorMessage =
    submitError instanceof Error ? submitError.message : null;

  return (
    <div className="sticky bottom-0 -mx-4 border-t border-border bg-bg-card px-4 py-3 sm:-mx-6 sm:px-6">
      <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-text-muted">
          {decisionCount === 0 ? (
            "No decisions yet — approve or reject rows above."
          ) : (
            <>
              <span className="text-emerald-300">{approvedCount} approved</span> ·{" "}
              <span className="text-red-300">{rejectedCount} rejected</span>
            </>
          )}
        </p>
        <div className="flex items-center gap-3">
          {errorMessage ? (
            <span className="text-xs text-red-300">{errorMessage}</span>
          ) : null}
          <Button
            variant="default"
            disabled={decisionCount === 0 || isSubmitting}
            onClick={onSubmit}
          >
            {isSubmitting ? "Submitting…" : `Submit ${decisionCount} decision${decisionCount === 1 ? "" : "s"}`}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Static deadline rendering — "in 60 hours" / "expired 3 hours ago".
 * Computed on render. The component does not auto-tick; that's
 * sufficient because TanStack Query refetches re-render this anyway,
 * and the deadline only matters to ~hour granularity.
 */
function DeadlineCountdown({ deadline }: { deadline: string }) {
  const dt = new Date(deadline);
  const ms = dt.getTime() - Date.now();
  const hours = Math.round(Math.abs(ms) / (60 * 60 * 1000));
  if (ms <= 0) {
    return (
      <span className="text-red-300">
        deadline expired {hours}h ago
      </span>
    );
  }
  return (
    <span className={hours <= 24 ? "text-amber-300" : "text-text-muted"}>
      {hours}h remaining
    </span>
  );
}
